import { time } from '@/shared/datetime/utils/time'

import type { CollectorDetailView, CollectorSummary } from './types'

/**
 * How often a collector read refetches while something on it is settling.
 *
 * Well under the collector's 60s heartbeat: the change being waited on lands
 * with a beat, and a poll no faster than the beat could show it almost a
 * minute late.
 */
export const SETTLING_POLL_INTERVAL = time(10, 's')

/**
 * Whether a collector, or a listener on it, is in an expected, temporary state
 * that resolves on its own — a collector `enrolling`/`enrolled` that has never
 * beaten, or a listener `setting_up` while its config rides down on the next
 * heartbeat. Every other status is settled, `error` and `disconnected`
 * included: those need a person, and polling would not change them.
 *
 * Takes the list row; a detail view is one too, structurally.
 */
export function isCollectorSettling(collector: CollectorSummary) {
	const awaitingFirstBeat =
		(collector.status === 'enrolling' || collector.status === 'enrolled') &&
		collector.lastSeen === null

	return (
		awaitingFirstBeat ||
		collector.logSources.some(({ status }) => status === 'setting_up')
	)
}

/**
 * Whether a collector has sent its first heartbeat. `enrolled` with no
 * heartbeat is not a check-in — it redeemed the token and has not reported.
 */
export function hasCheckedIn(
	collector: CollectorDetailView | null | undefined,
) {
	if (!collector) return false
	return (
		collector.lastSeen !== null &&
		collector.status !== 'enrolling' &&
		collector.status !== 'enrolled'
	)
}

/** `refetchInterval` for the list read: poll while any row is settling. */
export function listSettlingInterval(
	collectors: CollectorSummary[] | undefined,
) {
	return collectors?.some(isCollectorSettling) ? SETTLING_POLL_INTERVAL : false
}

/**
 * `refetchInterval` for the detail read. `null` is the 404 answer — nothing to
 * wait for.
 */
export function detailSettlingInterval(
	collector: CollectorDetailView | null | undefined,
) {
	return collector && isCollectorSettling(collector)
		? SETTLING_POLL_INTERVAL
		: false
}
